<template>
  <Toast ref="toastRef" :position="position" :group="group" :life="life" :showCloseIcon="showClose"
    :baseZIndex="baseZIndex" @show="onShow" @close="onClose" @clear-all="onClearAll" @click="onClick" :class="[
      customClass,
      {
        'p-toast-rtl': rtl,
        [`p-toast-${size}`]: size
      }
    ]" />
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import Toast from 'primevue/toast'
import { useToast } from 'primevue/usetoast'

export default defineComponent({
  name: 'MOPToast',
  components: {
    Toast
  },
  inheritAttrs: false
})
</script>

<script setup lang="ts">
import { ref, computed } from 'vue'
import type { MOPToastProps, MOPToastMessage } from './types'
import { useFormContext } from '../../../../composables/useFormContext'
import { useLocalization } from '../../../../composables/useLocalization'

const props = withDefaults(defineProps<MOPToastProps>(), {
  position: 'top-right',
  showClose: true,
  life: 3000,
  customClass: '',
  group: '',
  size: 'normal',
  rtl: false,
  'aria-label': 'Notifications',
  baseZIndex: 1000,
  enableTransitions: true,
  showErrors: true,
  validateOnMount: false,
  formContext: true,
  disabled: false
})

const emit = defineEmits<{
  'show': [message: MOPToastMessage]
  'close': [message: MOPToastMessage]
  'clear-all': []
  'click': [message: MOPToastMessage]
  'validation-error': [errors: string[]]
  'validation-success': []
}>()

// Refs and State
const toastRef = ref<InstanceType<typeof Toast> | null>(null)
const localValidationErrors = ref<string[]>([])
const isValidating = ref(false)

// Composables
const { formState } = useFormContext()
const { t } = useLocalization()
const toast = useToast()

// Computed
const currentErrors = computed(() => {
  return localValidationErrors.value
})

const localizedErrors = computed(() => {
  return currentErrors.value.map(error => {
    if (error.startsWith('validation.')) {
      return t(error)
    }
    return error
  })
})

// Methods
const getDefaultIcon = (severity?: string) => {
  switch (severity) {
    case 'success':
      return 'pi pi-check'
    case 'info':
      return 'pi pi-info-circle'
    case 'warn':
      return 'pi pi-exclamation-triangle'
    case 'error':
      return 'pi pi-times'
    default:
      return 'pi pi-info-circle'
  }
}

const validate = async (): Promise<boolean> => {
  if (!props.validationRules?.length) return true

  isValidating.value = true
  try {
    const errors: string[] = []

    for (const rule of props.validationRules) {
      if (rule.validator) {
        const isValid = await rule.validator('')
        if (!isValid && rule.message) {
          errors.push(rule.message)
        }
      }
    }

    localValidationErrors.value = errors
    if (errors.length > 0) {
      emit('validation-error', errors)
      return false
    }

    emit('validation-success')
    return true
  } finally {
    isValidating.value = false
  }
}

// Event Handlers
const onShow = (message: MOPToastMessage) => {
  emit('show', message)
}

const onClose = (message: MOPToastMessage) => {
  emit('close', message)
}

const onClearAll = () => {
  emit('clear-all')
}

const onClick = (message: MOPToastMessage) => {
  emit('click', message)
}

// Public Methods
const show = (message: MOPToastMessage) => {
  if (props.disabled) return
  toast.add(message)
}

const clear = () => {
  toast.removeAllGroups()
}

defineExpose({
  show,
  clear,
  validate,
  validationErrors: currentErrors,
  isValidating,
  isValid: computed(() => currentErrors.value.length === 0)
})
</script>
